import React, { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import queryString from 'query-string'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { useIsContentSearchActive } from 'src/Utils/search/search.hooks'
import { useSearchContext } from 'src/Utils/search/search.context'
import {
  ControlsAbsoluteRoutes,
  PoliciesAbsoluteRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { IsGatekeeperEnabled, GatekeeperFlag } from 'src/context/Gatekeeper'
import { useInvalidateControlsCache } from 'src/components/async/model/control'
import { generatePermalinkFromId } from '../../../components/PageLayout/Permalink'
import { UrlFragments } from '../../../Utils/globalEnums'
import { ControlGridRow } from './ControlsListItem.styles'
import {
  ControlsListItemAssigneesColumn,
  ControlsListItemOwnerColumn,
  ControlsListItemContentMatchColumn,
  ControlsListItemControlNameColumn,
  ControlsListItemDepartmentColumn,
  ControlsListItemEvidenceColumn,
  ControlsListItemFrameworksColumn,
  ControlsListItemMenuColumn,
  ControlsListItemModelIdColumn,
  ControlsListItemStatusColumn,
  ControlsListItemAIControlCheckColumns,
  LabelCountColumn,
} from './ControlsListItem.components'
import { ControlsListItemProps } from './ControlListItem.constants'
import { getControlsListItemConfig } from './ControlsListItem.helpers'

const ControlsListItemRow = ({
  control,
  audit,
  config,
  linkedPolicyNames = [],
  linkedDepartmentNames = [],
  showContentMatch,
}: ControlsListItemProps): JSX.Element => {
  const isContentSearchActive = useIsContentSearchActive()
  const { contentMatchIds } = useSearchContext()
  const params = useParams()
  const mutate = useInvalidateControlsCache()

  const isContentMatch = useMemo(
    () => contentMatchIds.has(control.getId()),
    [contentMatchIds, control],
  )
  const canViewAuditScans = useCanPerformAction(CAPABILITY.CAN_VIEW_AUDIT_SCANS)
  const showLabels = IsGatekeeperEnabled(GatekeeperFlag.LABELS)

  // toObject deep-copies the whole message — policies, labels, assignees and all — so
  // keep it to once per control rather than once per render, and hand every column the
  // same object so they can skip re-rendering too.
  const controlData = useMemo(() => control.toObject(), [control])
  const {
    id: controlId,
    modelId,
    departmentId,
    complianceFrameworkIdsList,
    status,
    policyId: policyModelId,
    migratedControlId,
  } = controlData

  const isNotApplicable = status === CONTROL_STATUS.NOTAPPLICABLE

  const isFromControlsIndex = location.pathname.includes(
    ControlsAbsoluteRoutes.INDEX,
  )
  const isFromPolicyShow = location.pathname.includes(
    PoliciesAbsoluteRoutes.SHOW,
  )
  const includeSearchParams = isFromControlsIndex || isFromPolicyShow
  const policyFilterFromUrl = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })[FilterParam.POLICY]
  const activePolicyFilter = Array.isArray(policyFilterFromUrl)
    ? policyFilterFromUrl[0]
    : policyFilterFromUrl
  const search = isFromPolicyShow
    ? {
        [FilterParam.POLICY]: [activePolicyFilter || policyModelId],
      }
    : undefined

  let toLink = generatePermalinkFromId({
    pageContext: params.pageContext as string,
    modelId,
    modelType: MODEL_TYPE.CONTROL,
    isInternalLink: true,
    includeSearchParams,
    search,
  })

  if (isFromPolicyShow) {
    toLink += `#${UrlFragments.POLICIES}`
  }

  const {
    showDepartmentColumn,
    showModelIdColumn,
    showControlNameColumn,
    showFrameworkColumn,
    showOperatingEffectivenessColumn,
    showLinkedEvidenceColumn,
    showStatusColumn,
    showOwnerColumn,
    showMenuColumn,
  } = getControlsListItemConfig(config)

  return (
    <ControlGridRow
      id={`controls-index-item-${modelId}`}
      as={Link}
      to={toLink}
      $isNotApplicable={isNotApplicable}
    >
      {(showContentMatch ?? (isFromControlsIndex && isContentSearchActive)) && (
        <ControlsListItemContentMatchColumn isContentMatch={isContentMatch} />
      )}
      {showDepartmentColumn && (
        <ControlsListItemDepartmentColumn
          controlId={controlId}
          show={!isNotApplicable}
          departmentId={departmentId}
          departmentNames={linkedDepartmentNames}
        />
      )}
      {showModelIdColumn && <ControlsListItemModelIdColumn modelId={modelId} />}
      {showControlNameColumn && (
        <ControlsListItemControlNameColumn
          control={controlData}
          isControlNa={isNotApplicable}
          policyNames={linkedPolicyNames}
        />
      )}
      {showLabels && <LabelCountColumn control={controlData} />}
      {showFrameworkColumn && (
        <ControlsListItemFrameworksColumn
          audit={audit}
          complianceFrameworkIds={complianceFrameworkIdsList}
        />
      )}
      {showLinkedEvidenceColumn && (
        <ControlsListItemEvidenceColumn
          show={!isNotApplicable}
          control={controlData}
        />
      )}

      {canViewAuditScans && (
        <ControlsListItemAIControlCheckColumns
          controlId={controlId}
          isNotApplicable={isNotApplicable}
          migratedControlId={migratedControlId}
          showOE={showOperatingEffectivenessColumn}
        />
      )}
      {showStatusColumn && (
        <ControlsListItemStatusColumn control={controlData} mutate={mutate} />
      )}
      {showOwnerColumn && (
        <ControlsListItemOwnerColumn control={controlData} mutate={mutate} />
      )}
      {showOwnerColumn && (
        <ControlsListItemAssigneesColumn
          control={controlData}
          onUpdate={mutate}
        />
      )}
      {showMenuColumn && (
        <ControlsListItemMenuColumn control={controlData} config={config} />
      )}
    </ControlGridRow>
  )
}

// A page of rows re-renders whenever anything above it does, and each row deep-copies its
// control and renders a dozen columns. Memoizing keeps that work tied to the row's own
// props changing. Callers must pass stable arrays for the linked names (see EMPTY_NAMES
// in ControlsIndexGrid.helpers) or this can never hit.
export const ControlsListItem = React.memo(ControlsListItemRow)
